import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './FieldSelector.css';

const FieldSelector = () => {
  usePageTitle('Odoo Field Selector');
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Model selection, 2: Field selection, 3: Config review
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [fields, setFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [expandedRelations, setExpandedRelations] = useState({});
  const [relatedFields, setRelatedFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');
  // Quality analysis state
  const [qualityData, setQualityData] = useState(null);
  const [loadingQuality, setLoadingQuality] = useState(false);
  const [showQualityView, setShowQualityView] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  // Ordering and Themes state
  const [orderedFields, setOrderedFields] = useState([]);
  const [fieldThemes, setFieldThemes] = useState({});
  const [themes, setThemes] = useState(['Standard']);
  const [draggedField, setDraggedField] = useState(null);

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      const response = await axiosInstance.get('/etl/flows/odoo_models/');
      setModels(response.data);
    } catch (err) {
      console.error('Failed to load models:', err);
    }
  };

  const selectModel = async (model) => {
    setLoading(true);
    setSelectedModel(model);

    try {
      const response = await axiosInstance.get('/etl/flows/odoo_fields/', {
        params: { model: model.model }
      });
      setFields(response.data.fields);
      setStep(2);

      // Auto-select common fields
      const autoSelect = response.data.fields
        .filter(f => ['id', 'name', 'create_date', 'write_date'].includes(f.name))
        .map(f => f.name);
      setSelectedFields(autoSelect);

    } catch (err) {
      console.error('Failed to load fields:', err);
      alert('Failed to load fields for this model');
    } finally {
      setLoading(false);
    }
  };

  const fetchQualityReport = async () => {
    if (!selectedModel) return;

    setLoadingQuality(true);
    try {
      const response = await axiosInstance.post('/etl/analyze-quality/', {
        model: selectedModel.model,
        fields: [],  // Empty = analyze all fields
        sample_size: 500
      });
      setQualityData(response.data);
      setShowQualityView(true);
      setSortBy('completeness'); // Auto-sort by completeness when loaded
    } catch (err) {
      console.error('Failed to analyze quality:', err);
      alert('Failed to analyze data quality: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoadingQuality(false);
    }
  };

  const getSortedFields = () => {
    if (!fields) return [];

    let sorted = [...fields];

    if (qualityData?.field_stats && sortBy !== 'name') {
      sorted.sort((a, b) => {
        const statsA = qualityData.field_stats[a.name] || {};
        const statsB = qualityData.field_stats[b.name] || {};

        if (sortBy === 'completeness') {
          return (statsB.completeness || 0) - (statsA.completeness || 0);
        } else if (sortBy === 'uniqueness') {
          return (statsB.uniqueness || 0) - (statsA.uniqueness || 0);
        }
        return 0;
      });
    } else {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    }

    return sorted;
  };

  const toggleField = (fieldName) => {
    if (selectedFields.includes(fieldName)) {
      setSelectedFields(selectedFields.filter(f => f !== fieldName));
    } else {
      setSelectedFields([...selectedFields, fieldName]);
    }
  };

  const expandRelation = async (field) => {
    const key = `${selectedModel.model}.${field.name}`;

    if (expandedRelations[key]) {
      // Collapse
      const newExpanded = { ...expandedRelations };
      delete newExpanded[key];
      setExpandedRelations(newExpanded);
      return;
    }

    // Expand - fetch related fields
    setLoading(true);
    try {
      const response = await axiosInstance.get('/etl/flows/odoo_related_fields/', {
        params: { model: selectedModel.model, field: field.name }
      });

      setRelatedFields({
        ...relatedFields,
        [key]: response.data.related_fields
      });
      setExpandedRelations({
        ...expandedRelations,
        [key]: true
      });
    } catch (err) {
      console.error('Failed to load related fields:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStepChange = (nextStep) => {
    if (nextStep === 3) {
      // Initialize orderedFields when entering Step 3
      setOrderedFields([...selectedFields]);
    }
    setStep(nextStep);
  };

  const handleDragStart = (e, fieldName) => {
    setDraggedField(fieldName);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetFieldName) => {
    e.preventDefault();
    if (draggedField === targetFieldName) return;

    const newOrderedFields = [...orderedFields];
    const draggedIdx = newOrderedFields.indexOf(draggedField);
    const targetIdx = newOrderedFields.indexOf(targetFieldName);

    newOrderedFields.splice(draggedIdx, 1);
    newOrderedFields.splice(targetIdx, 0, draggedField);
    setOrderedFields(newOrderedFields);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDraggedField(null);
  };

  const assignTheme = (fieldName, themeName) => {
    setFieldThemes({
      ...fieldThemes,
      [fieldName]: themeName
    });
  };

  const addTheme = () => {
    const name = prompt('Enter new theme name:');
    if (name && !themes.includes(name)) {
      setThemes([...themes, name]);
    }
  };

  const generateConfig = () => {
    const finalFields = orderedFields.length > 0
      ? orderedFields
      : selectedFields;

    const fieldNames = orderedFields.length > 0
      ? orderedFields
      : selectedFields;

    // Enrich field names with metadata if available
    const enrichedFields = fieldNames.map(name => {
      // Look in main fields
      const f = fields.find(field => field.name === name);
      if (f) return f;

      // Look in related fields
      for (const key in relatedFields) {
        const relF = relatedFields[key].find(rf => rf.dotted_name === name);
        if (relF) return {
          name: relF.dotted_name,
          label: relF.label,
          type: relF.type,
          is_related: true
        };
      }

      return { name };
    });

    const config = {
      model: selectedModel.model,
      fields: enrichedFields,
      field_themes: fieldThemes,
      themes: themes,
      domain: [],
      context: { lang: 'en_US' }
    };
    return config;
  };

  const createFlow = async () => {
    if (!flowName) {
      alert('Please enter a flow name');
      return;
    }

    const config = generateConfig();

    // Create the flow
    const flowData = {
      name: flowName,
      description: flowDescription || `ETL flow for ${selectedModel.name}`,
      odoo_model: selectedModel.model,
      config_file: `config/${selectedModel.model.replace('.', '_')}.json`,
      gcs_input_path: `gs://lamptwist/etl/${selectedModel.model.replace('.', '_')}/in/`,
      gcs_temp_path: `gs://lamptwist/etl/${selectedModel.model.replace('.', '_')}/temp/`,
      gcs_schema_path: `gs://lamptwist/bigquery_schema/bigquery_schema_${selectedModel.model.replace('.', '_')}.json`,
      bq_project_id: 'data-368909',
      bq_dataset: 'stg',
      bq_table: selectedModel.model.replace('.', '_'),
      bq_error_dataset: 'error',
      bq_error_table: selectedModel.model.replace('.', '_'),
      dataflow_region: 'europe-west1',
      execution_mode: 'direct_bq',
      extraction_config: config,
      status: 'active'
    };

    try {
      const response = await axiosInstance.post('/etl/flows/', flowData);
      alert(`Flow "${flowName}" created successfully!`);
      navigate(`/flows/${response.data.id}`);
    } catch (err) {
      alert('Failed to create flow: ' + (err.response?.data?.error || err.message));
    }
  };

  const getFieldIcon = (fieldType) => {
    const icons = {
      'char': '📝',
      'text': '📄',
      'integer': '🔢',
      'float': '💰',
      'boolean': '✓',
      'date': '📅',
      'datetime': '🕐',
      'many2one': '🔗',
      'one2many': '📋',
      'many2many': '🔗',
      'selection': '☑️',
    };
    return icons[fieldType] || '📌';
  };

  const organizeFields = (fieldsList) => {
    const categories = {
      core: [],
      relational: [],
      computed: [],
      other: []
    };

    fieldsList.forEach(field => {
      if (['id', 'name', 'display_name', 'create_date', 'write_date'].includes(field.name)) {
        categories.core.push(field);
      } else if (field.is_relational) {
        categories.relational.push(field);
      } else if (field.readonly) {
        categories.computed.push(field);
      } else {
        categories.other.push(field);
      }
    });

    return categories;
  };

  return (
    <div className="field-selector-container">
      {/* Progress Steps */}
      <div className="progress-steps">
        <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-label">Choose Model</div>
        </div>
        <div className="step-divider"></div>
        <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-label">Select Fields</div>
        </div>
        <div className="step-divider"></div>
        <div className={`step ${step >= 3 ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <div className="step-label">Review & Create</div>
        </div>
      </div>

      {/* Step 1: Model Selection */}
      {step === 1 && (
        <div className="step-content">
          <h2>Choose an Odoo Model</h2>
          <p className="step-description">Select the model you want to extract data from</p>

          <div className="models-grid">
            {models.map((model) => (
              <button
                key={model.model}
                className="model-card"
                onClick={() => selectModel(model)}
              >
                <div className="model-icon">{model.icon}</div>
                <div className="model-name">{model.name}</div>
                <div className="model-technical">{model.model}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Field Selection */}
      {step === 2 && selectedModel && (
        <div className="step-content">
          <div className="step-header">
            <div>
              <h2>Select Fields from {selectedModel.name}</h2>
              <p className="step-description">
                {showQualityView
                  ? 'Showing data quality metrics. Select fields based on completeness and data samples.'
                  : 'Choose the fields you want to extract. Click on relational fields to expand and select nested fields.'}
              </p>
            </div>
            <div className="header-stats">
              <span className="stat">{selectedFields.length} fields selected</span>

              {/* Quality analysis controls */}
              {!qualityData && !loadingQuality && (
                <button onClick={fetchQualityReport} className="btn-quality">
                  📊 Analyze Quality
                </button>
              )}
              {loadingQuality && (
                <span className="loading-quality">⏳ Analyzing...</span>
              )}
              {qualityData && (
                <>
                  <button
                    onClick={() => setShowQualityView(!showQualityView)}
                    className={`btn-toggle-view ${showQualityView ? 'active' : ''}`}
                  >
                    {showQualityView ? '📋 Normal View' : '📊 Quality View'}
                  </button>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select"
                  >
                    <option value="name">Sort: Name</option>
                    <option value="completeness">Sort: Completeness</option>
                    <option value="uniqueness">Sort: Uniqueness</option>
                  </select>
                </>
              )}

              <button
                onClick={() => handleStepChange(3)}
                className="btn-primary"
                disabled={selectedFields.length === 0}
              >
                Next: Review →
              </button>
            </div>
          </div>

          {/* Quality Summary Banner */}
          {qualityData && showQualityView && (
            <div className="quality-summary-header">
              <div className="quality-pill">
                <span className="pill-label">Analyzed:</span>
                <span className="pill-value">{qualityData.sample_size} records</span>
              </div>
              <div className="quality-pill">
                <span className="pill-label">Fields:</span>
                <span className="pill-value">{Object.keys(qualityData.field_stats || {}).length}</span>
              </div>
              <div className="quality-pill warning-pill">
                <span className="pill-label">Warnings:</span>
                <span className="pill-value">
                  {(qualityData.recommendations || []).filter(r => r.severity === 'warning').length}
                </span>
              </div>
              <div className="quality-pill error-pill">
                <span className="pill-label">Issues:</span>
                <span className="pill-value">
                  {(qualityData.recommendations || []).filter(r => r.severity === 'error').length}
                </span>
              </div>
            </div>
          )}

          <div className="fields-container">
            {showQualityView && qualityData ? (
              // Quality View - flat list sorted by quality metrics
              <div className="quality-fields-list">
                {getSortedFields().map((field) => {
                  const stats = qualityData.field_stats[field.name] || {};
                  const completeness = stats.completeness || 0;
                  const uniqueness = stats.uniqueness || 0;
                  const relationKey = `${selectedModel.model}.${field.name}`;
                  const isExpanded = expandedRelations[relationKey];
                  const hasRelatedFields = relatedFields[relationKey];

                  return (
                    <div key={field.name} className={`quality-field-group ${selectedFields.includes(field.name) ? 'selected' : ''}`}>
                      <div className="quality-field-item">
                        <label className="field-checkbox-row">
                          <input
                            type="checkbox"
                            checked={selectedFields.includes(field.name)}
                            onChange={() => toggleField(field.name)}
                          />
                          <div className="field-main-info">
                            <span className="field-icon">{getFieldIcon(field.type)}</span>
                            <span className="field-label">{stats.label || field.label}</span>
                            <code className="field-technical">{field.name}</code>
                          </div>
                          <div className="field-quality-bars">
                            <div className="quality-bar-container completeness">
                              <span className="bar-label">C</span>
                              <div className="quality-bar">
                                <div
                                  className={`quality-bar-fill ${completeness >= 0.95 ? 'good' : completeness >= 0.8 ? 'warning' : 'error'}`}
                                  style={{ width: `${completeness * 100}%` }}
                                />
                              </div>
                              <span className="bar-value">{(completeness * 100).toFixed(0)}%</span>
                            </div>
                            <div className="quality-bar-container uniqueness">
                              <span className="bar-label">U</span>
                              <div className="quality-bar">
                                <div
                                  className="quality-bar-fill unique"
                                  style={{ width: `${uniqueness * 100}%` }}
                                />
                              </div>
                              <span className="bar-value">{(uniqueness * 100).toFixed(0)}%</span>
                            </div>
                          </div>
                          {/* Distribution Bar (for low cardinality fields) */}
                          {stats.value_distribution && (
                            <div className="value-distribution-container">
                              <div className="distribution-bar">
                                {stats.value_distribution.map((dist, idx) => (
                                  <div
                                    key={idx}
                                    className="distribution-segment"
                                    style={{
                                      width: `${dist.percentage}%`,
                                      background: `hsl(${(idx * 137) % 360}, 65%, 65%)`
                                    }}
                                    title={`${dist.value}: ${dist.percentage}%`}
                                  />
                                ))}
                              </div>
                              <div className="distribution-legend">
                                {stats.value_distribution.map((dist, idx) => (
                                  <span key={idx} className="dist-tag">
                                    <span className="dot" style={{ background: `hsl(${(idx * 137) % 360}, 65%, 65%)` }}></span>
                                    {dist.value} ({dist.percentage}%)
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Sample Values (Horizontal) */}
                          {!stats.value_distribution && (
                            <div className="sample-values-inline">
                              {(stats.sample_values || []).map((val, idx) => (
                                <span key={idx} className="sample-tag">{String(val)}</span>
                              ))}
                            </div>
                          )}
                        </label>

                        {field.is_relational && (
                          <button
                            className="expand-btn"
                            onClick={() => expandRelation(field)}
                            title={`Expand ${field.relation}`}
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                        )}
                      </div>

                      {/* Expanded related fields in Quality View */}
                      {isExpanded && hasRelatedFields && (
                        <div className="related-fields quality-related">
                          <div className="related-header">
                            <span className="related-model">→ {field.relation}</span>
                            <button
                              className="select-all-btn"
                              onClick={() => {
                                const allRelated = hasRelatedFields.map(f => f.dotted_name);
                                const allSelected = allRelated.every(f => selectedFields.includes(f));
                                if (allSelected) {
                                  setSelectedFields(selectedFields.filter(f => !allRelated.includes(f)));
                                } else {
                                  setSelectedFields([...new Set([...selectedFields, ...allRelated])]);
                                }
                              }}
                            >
                              Select Common
                            </button>
                          </div>
                          <div className="related-fields-grid">
                            {hasRelatedFields.map((relField) => (
                              <label key={relField.dotted_name} className="related-field-label">
                                <input
                                  type="checkbox"
                                  checked={selectedFields.includes(relField.dotted_name)}
                                  onChange={() => toggleField(relField.dotted_name)}
                                />
                                <span className="field-icon small">{getFieldIcon(relField.type)}</span>
                                <span className="field-name">{relField.label}</span>
                                <code className="field-technical small">{relField.name}</code>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              // Normal Category View
              Object.entries(organizeFields(fields)).map(([category, categoryFields]) => {
                if (categoryFields.length === 0) return null;

                const categoryLabels = {
                  core: '⭐ Core Fields',
                  relational: '🔗 Related Objects',
                  computed: '🔢 Computed Fields',
                  other: '📋 Other Fields'
                };

                return (
                  <div key={category} className="field-category">
                    <h3>{categoryLabels[category]}</h3>
                    <div className="fields-list">
                      {categoryFields.map((field) => {
                        const relationKey = `${selectedModel.model}.${field.name}`;
                        const isExpanded = expandedRelations[relationKey];
                        const hasRelatedFields = relatedFields[relationKey];
                        const stats = qualityData?.field_stats?.[field.name];

                        return (
                          <div key={field.name} className="field-item-wrapper">
                            <div className="field-item">
                              <label className="field-label">
                                <input
                                  type="checkbox"
                                  checked={selectedFields.includes(field.name)}
                                  onChange={() => toggleField(field.name)}
                                />
                                <span className="field-icon">{getFieldIcon(field.type)}</span>
                                <div className="field-info">
                                  <div className="field-name-row">
                                    <span className="field-name">{field.label}</span>
                                    <code className="field-technical">{field.name}</code>
                                    {stats && (
                                      <span className={`completeness-badge ${stats.completeness >= 0.95 ? 'good' : stats.completeness >= 0.8 ? 'warning' : 'error'}`}>
                                        {(stats.completeness * 100).toFixed(0)}%
                                      </span>
                                    )}
                                  </div>
                                  <span className="field-type">{field.type}</span>
                                  {field.help && <span className="field-help">{field.help}</span>}
                                </div>
                              </label>

                              {field.is_relational && (
                                <button
                                  className="expand-btn"
                                  onClick={() => expandRelation(field)}
                                  title={`Expand ${field.relation}`}
                                >
                                  {isExpanded ? '▼' : '▶'}
                                </button>
                              )}
                            </div>

                            {/* Expanded related fields */}
                            {isExpanded && hasRelatedFields && (
                              <div className="related-fields">
                                <div className="related-header">
                                  <span className="related-model">→ {field.relation}</span>
                                  <button
                                    className="select-all-btn"
                                    onClick={() => {
                                      const allRelated = hasRelatedFields.map(f => f.dotted_name);
                                      const allSelected = allRelated.every(f => selectedFields.includes(f));
                                      if (allSelected) {
                                        setSelectedFields(selectedFields.filter(f => !allRelated.includes(f)));
                                      } else {
                                        setSelectedFields([...new Set([...selectedFields, ...allRelated])]);
                                      }
                                    }}
                                  >
                                    Select Common
                                  </button>
                                </div>
                                {hasRelatedFields.map((relField) => (
                                  <label key={relField.dotted_name} className="related-field-label">
                                    <input
                                      type="checkbox"
                                      checked={selectedFields.includes(relField.dotted_name)}
                                      onChange={() => toggleField(relField.dotted_name)}
                                    />
                                    <span className="field-icon small">{getFieldIcon(relField.type)}</span>
                                    <span className="field-name">{relField.label}</span>
                                    <code className="field-technical small">{relField.name}</code>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Step 3: Review & Create */}
      {step === 3 && (
        <div className="step-content">
          <div className="step-header">
            <div>
              <h2>Field Ordering & Themes</h2>
              <p className="step-description">Group your fields into themes and drag them into the desired order for the final table.</p>
            </div>
            <div className="header-actions">
              <button onClick={addTheme} className="btn-secondary">New Theme</button>
              <button onClick={() => setStep(2)} className="btn-secondary">Back</button>
            </div>
          </div>

          <div className="ordering-container">
            {themes.map(theme => (
              <div key={theme} className="theme-group">
                <div className="theme-header">
                  <h3>{theme}</h3>
                </div>
                <div className="theme-fields">
                  {orderedFields
                    .filter(f => (fieldThemes[f] || 'Standard') === theme)
                    .map((fieldName) => (
                      <div
                        key={fieldName}
                        className={`ordered-field-item ${draggedField === fieldName ? 'dragging' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, fieldName)}
                        onDragOver={(e) => handleDragOver(e, fieldName)}
                        onDrop={handleDrop}
                      >
                        <div className="field-drag-handle">⠿</div>
                        <div className="field-main">
                          <span className="field-name">{fieldName}</span>
                        </div>
                        <div className="field-theme-select">
                          <select
                            value={fieldThemes[fieldName] || 'Standard'}
                            onChange={(e) => assignTheme(fieldName, e.target.value)}
                          >
                            {themes.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      </div>
                    ))}
                  {orderedFields.filter(f => (fieldThemes[f] || 'Standard') === theme).length === 0 && (
                    <div className="empty-theme">No fields assigned. Drag fields here or change their theme.</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="review-footer">
            <div className="review-info-grid">
              <div className="review-section">
                <h3>Flow Details</h3>
                <div className="form-group">
                  <label>Flow Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={flowName}
                    onChange={(e) => setFlowName(e.target.value)}
                    placeholder="e.g. Sales Orders Pipeline"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-textarea"
                    value={flowDescription}
                    onChange={(e) => setFlowDescription(e.target.value)}
                    placeholder="Briefly describe what this flow does"
                    rows={3}
                  />
                </div>
              </div>

              <div className="review-section">
                <h3>Configuration Summary</h3>
                <div className="summary-list">
                  <div className="summary-row">
                    <span>Source Model:</span>
                    <code>{selectedModel.model}</code>
                  </div>
                  <div className="summary-row">
                    <span>Target Table:</span>
                    <code>{selectedModel.model.replace(/\./g, '_')}</code>
                  </div>
                  <div className="summary-row">
                    <span>Selected Fields:</span>
                    <span>{selectedFields.length} items</span>
                  </div>
                </div>
                <button onClick={createFlow} className="btn-primary create-btn" disabled={loading}>
                  {loading ? 'Creating Flow...' : 'Create Flow'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="loader">Loading...</div>
        </div>
      )}
    </div>
  );
};

export default FieldSelector;

